import React, { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { LangProviderContext } from './LangProviderContext';
import { getAppLangSelector, getAvailableCountries, getAvailableLocales, getUserLocale } from '../../app/appSelectors';
import { appInitAction, updateLangAction } from '../../app/appActions';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import type { ILocaleLang } from '../../app/types/appReducer.t';
import { getSystemLocale } from '../../app/appUtils';
import { ILangItem, ILangOptions, ILangParams, ILangPath } from './langProvider.t';
import { DEFAULT_LOCALE_LANG_ITEM } from './langConstants';
import getLangPath from './utils/getLangPath';

type IGetLangPath = (path?: ILangPath, params?: ILangParams, options?: ILangOptions) => string;

export interface ILangProvider {
    availableCountries: {};
    availableLocales: ILocaleLang[];
    lang: ILangItem | null;
    locale: string | null;
    localeLangItem: ILocaleLang,
    getLangPath: IGetLangPath;
    setLocale: (name: string) => void;
    setLang?: (name: string) => void;
}

export interface ILangProviderProps {
    children?: JSX.Element;
}

const LangProvider = (props: ILangProviderProps) => {
    const { children } = props;
    const dispatch = useDispatch();
    const availableCountries = useSelector(getAvailableCountries);
    const availableLocales = useSelector(getAvailableLocales);
    const lang = useSelector(getAppLangSelector);
    const locale = useSelector(getUserLocale);
    const _setLocale = useCallback((newLocale: string) => {
        if (locale !== newLocale) {
            dispatch(updateLangAction({ locale: newLocale, isRefreshApp: true }));
        }
    }, [locale, dispatch]);

    const _getLangPath = useCallback<IGetLangPath>(
        (path = '', params = {}, options = {}) => {
            return getLangPath(lang, path, params, options);
        },
        [lang],
    );

    useEffect(() => {
        const currentLocale = locale || getSystemLocale();
        document.documentElement.lang = currentLocale;
        moment.locale(currentLocale);
    }, [locale]);

    useEffect(() => {
        dispatch(appInitAction());
    }, [dispatch]);

    const localeLangItem: ILocaleLang = useMemo(() =>
        _.find(availableLocales, { value: locale }) || DEFAULT_LOCALE_LANG_ITEM, [availableLocales, locale]);

    return <>
        <LangProviderContext.Provider value={{
            availableCountries,
            availableLocales,
            getLangPath: _getLangPath,
            locale,
            localeLangItem,
            setLocale: _setLocale,
            setLang: _setLocale,
            lang,
        }}>
            {children}
        </LangProviderContext.Provider>
    </>;
};


export default LangProvider;
